import React, { useState, useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  AppBar,
  Toolbar,
  Button,
  Chip,
  Menu,
  MenuItem,
  Avatar,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
} from '@mui/material';
import {
  VideoLibrary as VideoLibraryIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
  People as PeopleIcon,
  Mic as MicIcon,
  Bedtime as BedtimeIcon,
  Folder as FolderIcon,
  AutoStories as AutoStoriesIcon,
  PermMedia as PermMediaIcon,
  Menu as MenuIcon,
  Insights as InsightsIcon,
  Lightbulb as LightbulbIcon,
  ReceiptLong as ReceiptLongIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import Login from './components/Login';
import UserManagementPage from './pages/UserManagementPage';
import TranscriptionPage from './pages/TranscriptionPage';
import SleepPromptSystemEditPage from './pages/SleepPromptSystemEditPage';
import SleepVideosV3Page from './pages/SleepVideosV3Page';
import SleepVideoV3DetailPage from './pages/SleepVideoV3DetailPage';
import VoiceOverConfigsPage from './pages/VoiceOverConfigsPage';
import ChannelsPage from './pages/ChannelsPage';
import AnalyticsPage from './pages/analytics/AnalyticsPage';
import ChannelAnalyticsPage from './pages/analytics/ChannelAnalyticsPage';
import ExpensesPage from './pages/expenses/ExpensesPage';
import VideoIdeasPage from './pages/VideoIdeasPage';
import ThumbnailsPage from './pages/thumbnails/ThumbnailsPage';
import OpenCodeChatPage from './pages/opencode-chat/OpenCodeChatPage';
import StoryScriptsPage from './pages/StoryScriptsPage';
import StoryScriptDetailPage from './pages/StoryScriptDetailPage';
import AiStoriesPromptSystemEditPage from './pages/AiStoriesPromptSystemEditPage';
import VideoFootagePage from './pages/VideoFootagePage';
import VideoFootageFolderPage from './pages/VideoFootageFolderPage';
import VideoFootageCutPage from './pages/VideoFootageCutPage';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { gradients } from './theme';
import AuthService from './services/AuthService';
import { roleAllowed } from './utils/roles';
import { UploadProvider, useUploadContext } from './contexts/UploadContext';
import { AdvancedFeaturesProvider } from './contexts/AdvancedFeaturesContext';
import './App.css';

// Everyone who owns channels gets Analytics; only partners see revenue there.
// Partners also get every admin page.
const ANALYTICS_ROLES = ['partner', 'admin', 'superadmin'];
// Expenses count against revenue, so only partners track them.
const EXPENSE_ROLES = ['partner'];

// Navigation configuration: defines which roles can access which pages
// Format: { path, label, icon, allowedRoles: ['role1', 'role2', ...] }
const NAVIGATION_ITEMS = [
  {
    path: '/ai-stories',
    label: 'AI Stories',
    icon: AutoStoriesIcon,
    allowedRoles: ['editor', 'admin', 'superadmin'],
  },
  {
    path: '/video-ideas',
    label: 'Ideas',
    icon: LightbulbIcon,
    allowedRoles: ['admin', 'superadmin'],
  },
  {
    path: '/thumbnails',
    label: 'Thumbnails',
    icon: ImageIcon,
    allowedRoles: ['admin', 'superadmin'],
  },
  {
    path: '/transcription',
    label: 'Transcription',
    icon: MicIcon,
    allowedRoles: ['editor', 'admin', 'superadmin', 'ugcCreator'],
    hidden: true,
  },
  {
    path: '/sleep-videos',
    label: 'Sleep Videos',
    icon: BedtimeIcon,
    allowedRoles: ['admin', 'superadmin'],
  },
  {
    path: '/video-footage',
    label: 'Video Footage',
    icon: PermMediaIcon,
    allowedRoles: ['admin', 'superadmin', 'uploader'],
  },
  {
    path: '/channels',
    label: 'Channels',
    icon: FolderIcon,
    allowedRoles: ['admin', 'superadmin'],
  },
  {
    path: '/analytics',
    label: 'Analytics',
    icon: InsightsIcon,
    allowedRoles: ANALYTICS_ROLES,
  },
  {
    path: '/expenses',
    label: 'Expenses',
    icon: ReceiptLongIcon,
    allowedRoles: EXPENSE_ROLES,
  },
  {
    path: '/users',
    label: 'User Management',
    icon: PeopleIcon,
    allowedRoles: ['superadmin'],
  },
];

// Helper function to get default route based on user role
const getDefaultRoute = (role) => {
  if (role === 'editor' || role === 'admin' || role === 'superadmin') return '/ai-stories';
  if (role === 'uploader') return '/video-footage';
  if (role === 'ugcCreator') return '/transcription';
  if (role === 'partner') return '/analytics';
  return '/ai-stories';
};

// Helper for nav button styling (theme passed from Layout)
const getNavButtonSx = (isActive, contrastText) => ({
  color: contrastText,
  textTransform: 'none',
  fontWeight: isActive ? 700 : 400,
  borderBottom: isActive
    ? `2px solid ${contrastText}`
    : '2px solid transparent',
  borderRadius: 0,
  px: 2,
});

// Helper function to check if user role has access to a navigation item
const hasAccess = (userRole, allowedRoles) => {
  return roleAllowed(userRole, allowedRoles);
};

// Layout component that includes the navigation bar
function Layout({ children, user, onLogout }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { isUploading } = useUploadContext();
  const contrastText = theme.palette.primary.contrastText;

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const closeMobileNav = () => {
    setMobileNavOpen(false);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await onLogout();
    navigate('/login');
  };

  // Handle navigation with upload check
  const handleNavigation = (path) => {
    if (isUploading) {
      const shouldProceed = window.confirm(
        'Upload is in progress. Are you sure you want to leave this page? Your upload may be interrupted.',
      );
      if (!shouldProceed) {
        return;
      }
    }
    navigate(path);
    closeMobileNav();
  };

  // Handle browser tab/window close or refresh
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isUploading) {
        e.preventDefault();
        // Modern browsers ignore custom messages and show their own
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isUploading]);

  const getRoleColor = (role) => {
    switch (role) {
      case 'superadmin':
        return 'error';
      case 'admin':
        return 'warning';
      case 'uploader':
        return 'info';
      case 'ugcCreator':
        return 'info';
      case 'editor':
        return 'success';
      case 'partner':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const isActive = (path) => {
    if (path === '/channels') {
      return (
        location.pathname === '/channels' ||
        location.pathname.startsWith('/channels/')
      );
    }
    if (path === '/video-footage') {
      return (
        location.pathname === '/video-footage' ||
        location.pathname.startsWith('/video-footage/')
      );
    }
    if (path === '/analytics') {
      return location.pathname.startsWith('/analytics');
    }
    return location.pathname === path;
  };

  const NavButton = ({ path, icon: Icon, label }) => (
    <Button
      onClick={() => handleNavigation(path)}
      sx={getNavButtonSx(isActive(path), contrastText)}
    >
      <Icon sx={{ mr: 1, fontSize: 20 }} />
      {label}
    </Button>
  );

  return (
    <Box
      sx={{
        flexGrow: 1,
        minHeight: '100vh',
        background: gradients.primary,
        pb: 4,
      }}
    >
      <AppBar
        position='static'
        elevation={0}
        sx={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        <Toolbar sx={{ py: 1.5 }}>
          <Box
            onClick={() => {
              if (isUploading) {
                const shouldProceed = window.confirm(
                  'Upload is in progress. Are you sure you want to leave this page? Your upload may be interrupted.',
                );
                if (!shouldProceed) {
                  return;
                }
              }
              window.location.href = '/';
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            <VideoLibraryIcon
              sx={{ mr: 2, fontSize: 32, color: contrastText }}
            />
            <Typography
              variant='h5'
              component='div'
              sx={{
                fontWeight: 700,
                color: contrastText,
                letterSpacing: '0.5px',
              }}
            >
              Video Helper
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1, display: 'flex', gap: 2, ml: { xs: 1.5, md: 4 } }}>
            {!isMobile &&
              NAVIGATION_ITEMS.map((item) => {
                if (item.hidden || !hasAccess(user?.role, item.allowedRoles)) {
                  return null;
                }
                return (
                  <NavButton
                    key={item.path}
                    path={item.path}
                    icon={item.icon}
                    label={item.label}
                  />
                );
              })}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isMobile && (
              <IconButton
                onClick={() => setMobileNavOpen(true)}
                sx={{ color: contrastText }}
                aria-label='Open navigation menu'
              >
                <MenuIcon />
              </IconButton>
            )}
            <Chip
              label={user?.role || 'user'}
              color={getRoleColor(user?.role)}
              size='small'
              sx={{
                fontWeight: 600,
                textTransform: 'capitalize',
                color: contrastText,
                borderColor: 'rgba(255, 255, 255, 0.5)',
                display: { xs: 'none', sm: 'inline-flex' },
              }}
              variant='outlined'
            />
            <Button
              onClick={handleMenuOpen}
              sx={{
                color: contrastText,
                textTransform: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                }}
              >
                <AccountCircleIcon />
              </Avatar>
              <Typography variant='body2' sx={{ fontWeight: 500 }}>
                {user?.username || 'User'}
              </Typography>
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        anchor='left'
        open={mobileNavOpen}
        onClose={closeMobileNav}
        PaperProps={{ sx: { width: 280 } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 1 }}>
            Navigate
          </Typography>
          <List>
            {NAVIGATION_ITEMS.map((item) => {
              if (item.hidden || !hasAccess(user?.role, item.allowedRoles)) {
                return null;
              }
              const Icon = item.icon;
              return (
                <ListItemButton
                  key={item.path}
                  selected={isActive(item.path)}
                  onClick={() => handleNavigation(item.path)}
                >
                  <ListItemIcon>
                    <Icon fontSize='small' />
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      </Drawer>
      <Container
        maxWidth={false}
        sx={{
          mt: 4,
          mb: 4,
          px: { xs: 2, sm: 3, md: 8, lg: 10 },
        }}
      >
        {children}
      </Container>
    </Box>
  );
}

// Protected Route component
function ProtectedRoute({ children, user, requiredRole, allowedRoles }) {
  if (!user) {
    return <Navigate to='/login' replace />;
  }

  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to={getDefaultRoute(user.role)} replace />;
  }

  if (allowedRoles && !roleAllowed(user.role, allowedRoles)) {
    return <Navigate to={getDefaultRoute(user.role)} replace />;
  }

  return children;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      if (AuthService.isAuthenticated()) {
        // Verify token is still valid by fetching current user
        const currentUser = await AuthService.getCurrentUser();
        setUser(currentUser);
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      // Token is invalid or expired
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = async () => {
    await checkAuth();
  };

  const handleLogout = async () => {
    try {
      await AuthService.logout();
      setIsAuthenticated(false);
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local state even if API call fails
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  if (loading) {
    return null; // Or a loading spinner
  }

  return (
    <BrowserRouter>
      <UploadProvider>
        <AdvancedFeaturesProvider>
          <Routes>
            <Route
              path='/login'
              element={
                isAuthenticated ? (
                  <Navigate to={getDefaultRoute(user?.role)} replace />
                ) : (
                  <Login onLoginSuccess={handleLoginSuccess} />
                )
              }
            />
            <Route
              path='/users'
              element={
                <ProtectedRoute user={user} requiredRole='superadmin'>
                  <Layout user={user} onLogout={handleLogout}>
                    <UserManagementPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/transcription'
              element={
                <ProtectedRoute
                  user={user}
                  allowedRoles={[
                    'editor',
                    'admin',
                    'superadmin',
                    'ugcCreator',
                  ]}
                >
                  <Layout user={user} onLogout={handleLogout}>
                    <TranscriptionPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/sleep-videos'
              element={
                <ProtectedRoute
                  user={user}
                  allowedRoles={['admin', 'superadmin']}
                >
                  <Layout user={user} onLogout={handleLogout}>
                    <SleepVideosV3Page />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/sleep-videos/prompt-system/edit'
              element={
                <ProtectedRoute
                  user={user}
                  allowedRoles={['admin', 'superadmin']}
                >
                  <Layout user={user} onLogout={handleLogout}>
                    <SleepPromptSystemEditPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/sleep-videos/voice-over-configs'
              element={
                <ProtectedRoute
                  user={user}
                  allowedRoles={['admin', 'superadmin']}
                >
                  <Layout user={user} onLogout={handleLogout}>
                    <VoiceOverConfigsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/sleep-videos/:id'
              element={
                <ProtectedRoute
                  user={user}
                  allowedRoles={['admin', 'superadmin']}
                >
                  <Layout user={user} onLogout={handleLogout}>
                    <SleepVideoV3DetailPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/video-footage/cut'
              element={
                <ProtectedRoute
                  user={user}
                  allowedRoles={['admin', 'superadmin']}
                >
                  <Layout user={user} onLogout={handleLogout}>
                    <VideoFootageCutPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/video-footage/:folderId'
              element={
                <ProtectedRoute
                  user={user}
                  allowedRoles={['admin', 'superadmin', 'uploader']}
                >
                  <Layout user={user} onLogout={handleLogout}>
                    <VideoFootageFolderPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/video-footage'
              element={
                <ProtectedRoute
                  user={user}
                  allowedRoles={['admin', 'superadmin', 'uploader']}
                >
                  <Layout user={user} onLogout={handleLogout}>
                    <VideoFootagePage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/channels'
              element={
                <ProtectedRoute
                  user={user}
                  allowedRoles={['admin', 'superadmin']}
                >
                  <Navigate to='/channels/ai-stories' replace />
                </ProtectedRoute>
              }
            />
            {['/analytics', '/analytics/:tab'].map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <ProtectedRoute user={user} allowedRoles={ANALYTICS_ROLES}>
                    <Layout user={user} onLogout={handleLogout}>
                      <AnalyticsPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />
            ))}
            <Route
              path='/expenses'
              element={
                <ProtectedRoute user={user} allowedRoles={EXPENSE_ROLES}>
                  <Layout user={user} onLogout={handleLogout}>
                    <ExpensesPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/thumbnails'
              element={
                <ProtectedRoute
                  user={user}
                  allowedRoles={['admin', 'superadmin']}
                >
                  <Layout user={user} onLogout={handleLogout}>
                    <ThumbnailsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/video-ideas'
              element={
                <ProtectedRoute
                  user={user}
                  allowedRoles={['admin', 'superadmin']}
                >
                  <Layout user={user} onLogout={handleLogout}>
                    <VideoIdeasPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/video-ideas/opencode-chat'
              element={
                <ProtectedRoute user={user} allowedRoles={['superadmin']}>
                  <Layout user={user} onLogout={handleLogout}>
                    <OpenCodeChatPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/analytics/channels/:id'
              element={
                <ProtectedRoute user={user} allowedRoles={ANALYTICS_ROLES}>
                  <Layout user={user} onLogout={handleLogout}>
                    <ChannelAnalyticsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/channels/:tab'
              element={
                <ProtectedRoute
                  user={user}
                  allowedRoles={['admin', 'superadmin']}
                >
                  <Layout user={user} onLogout={handleLogout}>
                    <ChannelsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/ai-stories'
              element={
                <ProtectedRoute
                  user={user}
                  allowedRoles={['editor', 'admin', 'superadmin']}
                >
                  <Layout user={user} onLogout={handleLogout}>
                    <StoryScriptsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/ai-stories/voice-over-configs'
              element={
                <ProtectedRoute
                  user={user}
                  allowedRoles={['admin', 'superadmin']}
                >
                  <Layout user={user} onLogout={handleLogout}>
                    <VoiceOverConfigsPage
                      scope='ai-stories'
                      backTo='/ai-stories'
                      backLabel='Back to AI Stories'
                      pageTitle='Voice Over Configs (AI Stories)'
                      emptyHint='No voice over configs yet. Create one to use for AI Stories scripts.'
                    />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/ai-stories/prompt-system/edit'
              element={
                <ProtectedRoute
                  user={user}
                  allowedRoles={['admin', 'superadmin']}
                >
                  <Layout user={user} onLogout={handleLogout}>
                    <AiStoriesPromptSystemEditPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/ai-stories/:id'
              element={
                <ProtectedRoute
                  user={user}
                  allowedRoles={['editor', 'admin', 'superadmin']}
                >
                  <Layout user={user} onLogout={handleLogout}>
                    <StoryScriptDetailPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path='/'
              element={<Navigate to={getDefaultRoute(user?.role)} replace />}
            />
            <Route
              path='*'
              element={<Navigate to={getDefaultRoute(user?.role)} replace />}
            />
          </Routes>
        </AdvancedFeaturesProvider>
      </UploadProvider>
    </BrowserRouter>
  );
}

export default App;
